"use client";

import Image from "next/image";
import { env } from "@/env";
import { Icon } from "@prisma/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Button from "../ui/Button";
import { LucideDownload, LucidePalette } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

interface IconsProps {
  icons: Icon[];
}

export default function Icons({ icons }: IconsProps) {
  const handleDownload = async ({
    url,
    fileName,
  }: {
    url: string;
    fileName: string;
  }) => {
    try {
      const res = await fetch(url, { method: "GET" });

      const blob = await res.blob();
      const newBlob = new Blob([blob]);

      const blobUrl = window.URL.createObjectURL(newBlob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `${fileName}.png`);
      document.body.appendChild(link);
      link.click();
      link?.parentNode?.removeChild(link);

      // clean up Url
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error(
        "Failed to download image, try using right click and save as",
      );
    }
  };

  return (
    <TooltipProvider>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-7">
        {icons.map((icon) => (
          <Dialog key={icon.id}>
            <DialogTrigger>
              <motion.div
                whileHover={{
                  scale: 1.1,
                  transition: {
                    duration: 0.2,
                  },
                }}
                className="relative h-full w-full rounded-2xl"
              >
                <Image
                  src={`${env.NEXT_PUBLIC_CLOUDFRONT_URL}/${icon.id}`}
                  alt="AI Generated Icon"
                  width={1024}
                  height={1024}
                  className="rounded-2xl"
                />
                {icon.addColorElementToDescription && (
                  <div className="absolute left-2 top-2 z-10 ">
                    <Tooltip delayDuration={100}>
                      <TooltipTrigger asChild>
                        <Image
                          className="h-6 w-6 rounded-full"
                          src="/chroma_imagine.jpg"
                          alt="Chroma Imagine"
                          width={1024}
                          height={1024}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="">Chroma Imagine</div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </motion.div>
            </DialogTrigger>
            <DialogContent
              onOpenAutoFocus={(e) => e.preventDefault()}
              className="flex w-full flex-col items-center gap-10 md:max-w-4xl md:flex-row"
            >
              <DialogHeader>
                <DialogTitle hidden>Custom logo</DialogTitle>
                <DialogDescription hidden>
                  Custom logo generated by AI
                </DialogDescription>
              </DialogHeader>
              <Image
                className="h-32 w-32 rounded-xl md:h-96 md:w-96"
                src={`${env.NEXT_PUBLIC_CLOUDFRONT_URL}/${icon.id}`}
                alt="AI Generated Icon"
                width={1024}
                height={1024}
              />
              <div className="flex w-full flex-col gap-4">
                <p className="w-full">
                  <span className="font-semibold">Description</span>:{" "}
                  {icon.description.length > 300
                    ? icon.description.substring(0, 300) + "..."
                    : icon.description}
                </p>
                <p>
                  <span className="font-semibold">Style</span>: {icon.style}
                </p>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Color</span>:{" "}
                  <Tooltip delayDuration={100}>
                    <TooltipTrigger>
                      <div
                        className="h-8 w-8 rounded-full"
                        style={{
                          backgroundColor: icon.color,
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{icon.color}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <p>
                  <span className="font-semibold">Complexity</span>:{" "}
                  {icon.complexity}
                </p>
                <div className="flex w-full flex-col gap-4">
                  {/* <Button className="flex-1">Create variant (uses credit)</Button> */}
                  <div className="flex w-full gap-4">
                    <a
                      className="w-full flex-1"
                      href={`/generate?description=${encodeURIComponent(icon.description)}&colorHex=${encodeURIComponent(icon.color)}&style=${icon.style}&backgroundColor=${icon.backgroundColor}&addColorElementToDescription=${icon.addColorElementToDescription ? "true" : "false"}&complexity=${icon.complexity}`}
                    >
                      <Button className="w-full">
                        Reprompt
                        <LucidePalette className="ml-2" />
                      </Button>
                    </a>
                    <Button
                      onClick={async () =>
                        await handleDownload({
                          url: `${env.NEXT_PUBLIC_CLOUDFRONT_URL}/${icon.id}`,
                          fileName: `${icon.description}`,
                        })
                      }
                      className="w-full flex-1 px-0"
                    >
                      Download
                      <LucideDownload className="ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        ))}
      </div>
    </TooltipProvider>
  );
}
